import { memo, useEffect, useMemo, useState } from 'react';

import Debug from '@/components/Debug';
import ReportIssueButton from '@/components/ReportIssueButton';
import { JOTFORM_VERIFIABLE_MISSING_FORM_URL } from '@/constants/jotform';
import { useStaffEnrollment } from '@/hooks/useStaffEnrollment';
import { Alert } from '@/ui/Alert';
import DeprecatedAlert from '@/ui/components/Alert';

import { StaffEnrollmentWarningProps, StaffEnrollmentWithDebug } from './types';

const Step = ({ label, count, success }: { label: string; count: number; success: boolean }) => (
  <li className="flex items-center justify-between py-1 border-b last:border-0 border-gray-100">
    <span className="flex items-center gap-2 text-sm">
      <span>{success ? '✅' : '❌'}</span>
      <span>{label}</span>
    </span>
    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">{count}</span>
  </li>
);

const StaffEnrollmentWarning = ({
  staffId,
  personId,
  staffName,
  insurance,
  insuranceType,
  jurisdiction,
  programScope
}: StaffEnrollmentWarningProps) => {
  const [shouldFetchDebug, setShouldFetchDebug] = useState(false);

  const manualParams = useMemo(
    () =>
      !personId && jurisdiction
        ? {
            jurisdiction,
            insuranceProvider: insurance,
            insuranceType,
            programScope
          }
        : undefined,
    [personId, jurisdiction, insurance, insuranceType, programScope]
  );

  const { data: staffEnrollmentDebugData } = useStaffEnrollment(
    staffId,
    personId,
    true,
    shouldFetchDebug,
    manualParams
  );

  useEffect(() => {
    if (staffId && (personId || manualParams)) {
      setShouldFetchDebug(true);
    }
  }, [staffId, personId, manualParams]);

  const extraParameters = {
    insurance: insurance ?? '',
    jurisdiction: jurisdiction ?? '',
    provider: staffName ?? ''
  };

  const joinedInfo = [jurisdiction, insurance, insuranceType].filter(Boolean).join(', ') || '';

  const debugInfo = (staffEnrollmentDebugData as StaffEnrollmentWithDebug | undefined)?.debug_info;

  if (!insurance) {
    return null;
  }

  return (
    <div>
      {/* Paragraphs and a button to press: more than the one line a banner holds. */}
      <Alert variant="warning">
        <p>
          The selected provider in Healthie IS MISSING credentialed enrollment with this
          person&apos;s insurance provider
        </p>
        {staffName && (
          <p>
            <strong>{staffName}</strong>
          </p>
        )}
        {joinedInfo && <p>{joinedInfo}</p>}
        <div className="mt-2">
          <ReportIssueButton
            report_url={JOTFORM_VERIFIABLE_MISSING_FORM_URL}
            extraParameters={extraParameters}
          />
        </div>
      </Alert>
      {debugInfo && debugInfo.error_message ? (
        <div className="mt-2">
          <DeprecatedAlert level="error" dismissable={false}>
            <h4 className="font-bold text-sm mb-1">Enrollment Lookup Error</h4>
            <p className="text-sm">{debugInfo.error_message || 'An unknown error occurred'}</p>
          </DeprecatedAlert>
        </div>
      ) : (
        debugInfo && (
          <div className="mt-2 p-3 bg-white border rounded shadow-sm">
            <h4 className="font-bold text-sm mb-2 text-gray-700">Enrollment Lookup Debug</h4>
            <ul className="space-y-0">
              <Step
                label="Staff Match"
                count={debugInfo.counts.after_staff_check}
                success={debugInfo.filters.staff_match}
              />
              <Step
                label="Jurisdiction"
                count={debugInfo.counts.after_jurisdiction}
                success={debugInfo.filters.jurisdiction_match}
              />
              <Step
                label={`Verifiable Name: ${debugInfo.resolved.verifiable_name || 'None'}`}
                count={debugInfo.counts.after_verifiable_name}
                success={debugInfo.filters.verifiable_name_match}
              />
              <Step
                label={`Line of Business: ${
                  Array.isArray(debugInfo.resolved.line_of_business)
                    ? debugInfo.resolved.line_of_business.join(', ')
                    : debugInfo.resolved.line_of_business || 'None'
                }`}
                count={debugInfo.counts.after_line_of_business}
                success={debugInfo.filters.line_of_business_match}
              />
              <Step
                label="Date (Effective/Terminated)"
                count={debugInfo.counts.after_date_check}
                success={debugInfo.filters.date_match}
              />
              <Step
                label="Program Scope"
                count={debugInfo.counts.after_program_check}
                success={debugInfo.filters.program_match}
              />
            </ul>
          </div>
        )
      )}
      {staffEnrollmentDebugData && !debugInfo && (
        <div className="relative right-1 bottom-1">
          <Debug object={staffEnrollmentDebugData} />
        </div>
      )}
    </div>
  );
};

export default memo(StaffEnrollmentWarning);
